import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { API, LogoMark, saveSession } from "@/lib/site";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/60";

export default function Auth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const url = mode === "login" ? `${API}/auth/login` : `${API}/auth/register`;
      const payload = mode === "login" ? { email, password } : { name, email, password };
      const { data } = await axios.post(url, payload);
      saveSession(data.token, data.user);
      toast.success(mode === "login" ? `Welcome back, ${data.user.name || data.user.email}.` : "Account created — welcome to Wynova Cloud.");
      navigate(data.user.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen technical-grid bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 sm:p-10 shadow-2xl shadow-slate-900/10">
          <div className="flex items-center gap-3 mb-2">
            <LogoMark className="w-10 h-10" />
            <div>
              <h1 className="font-display font-bold text-lg tracking-tight">Wynova Cloud</h1>
              <p className="text-xs text-slate-500">Client portal — servers, hosting & email</p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-1 p-1 rounded-full bg-slate-100 dark:bg-slate-800">
            {["login", "register"].map((m) => (
              <button key={m} type="button" onClick={() => { setMode(m); setError(""); }}
                      data-testid={m === "login" ? "auth-mode-login" : "auth-mode-register"}
                      className={`py-2 rounded-full text-sm font-semibold transition-colors ${
                        mode === m ? "bg-primary text-white" : "text-slate-500 dark:text-slate-400"
                      }`}>
                {m === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="mt-6">
            {mode === "register" && (
              <input required minLength={2} placeholder="Your name" value={name} data-testid="auth-name"
                     onChange={(e) => setName(e.target.value)} className={`${inputCls} mb-4`} />
            )}
            <input type="email" required placeholder="Email address" value={email} data-testid="auth-email"
                   onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mb-4`} />
            <input type="password" required minLength={mode === "register" ? 8 : 1}
                   placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
                   value={password} data-testid="auth-password"
                   onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            {error && <p data-testid="auth-error" className="mt-3 text-sm text-red-500">{error}</p>}
            <button type="submit" disabled={loading} data-testid="auth-submit"
                    className="mt-6 w-full inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-60 transition-colors">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Sign In to Portal" : "Create My Account"}
            </button>
          </form>
          <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-primary">
            <ArrowLeft className="w-3 h-3" /> Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}
